import { useState } from "react";
import { Mail, Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createEnquiry } from "@/lib/api";
import { Reveal } from "./motion";
import { scrollTo } from "./Header";
import { NAV } from "./data";

const EMAIL = "Info@prismclinic.com";

export const Footer = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all fields.");
      return;
    }
    setSending(true);
    try {
      await createEnquiry(form);
      toast.success("Thank you — your message has been sent.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="contact" data-testid="footer-section" className="relative bg-primary text-primary-foreground overflow-hidden">
      <span className="absolute inset-x-0 top-0 h-1.5 prism-line" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left — CTA + enquiry */}
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary-foreground/60 mb-6">
                Get in touch
              </p>
              <h2 className="font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
                Have a question before
                <br /> you book?
              </h2>
              <p className="mt-6 max-w-md font-light text-primary-foreground/70">
                Send a short message and Dr. Fee will reply personally. There&apos;s
                no pressure — just a first conversation.
              </p>

              <a
                href={`mailto:${EMAIL}`}
                data-testid="footer-email-link"
                className="mt-8 inline-flex items-center gap-3 font-serif text-2xl sm:text-3xl font-light hover:opacity-80 transition-opacity"
              >
                <Mail size={22} /> {EMAIL}
              </a>
            </Reveal>

            <Reveal delay={0.1}>
              <form onSubmit={submit} className="mt-12 space-y-5 max-w-md" data-testid="enquiry-form">
                <div>
                  <Label htmlFor="c-name" className="text-sm text-primary-foreground/80">Name</Label>
                  <Input
                    id="c-name"
                    data-testid="enquiry-name"
                    value={form.name}
                    onChange={set("name")}
                    className="mt-2 h-12 rounded-xl bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label htmlFor="c-email" className="text-sm text-primary-foreground/80">Email</Label>
                  <Input
                    id="c-email"
                    type="email"
                    data-testid="enquiry-email"
                    value={form.email}
                    onChange={set("email")}
                    className="mt-2 h-12 rounded-xl bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40"
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="c-message" className="text-sm text-primary-foreground/80">Message</Label>
                  <Textarea
                    id="c-message"
                    data-testid="enquiry-message"
                    value={form.message}
                    onChange={set("message")}
                    className="mt-2 rounded-xl bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 min-h-[110px]"
                    placeholder="How can I help?"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="enquiry-submit"
                  disabled={sending}
                  variant="secondary"
                  className="h-12 rounded-full px-8 py-3 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                >
                  {sending ? (
                    <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Sending…</span>
                  ) : (
                    "Send message"
                  )}
                </Button>
              </form>
            </Reveal>
          </div>

          {/* Right — brand + nav */}
          <div className="lg:col-span-6 lg:pl-16 flex flex-col justify-between">
            <Reveal delay={0.15}>
              <div className="inline-flex items-center rounded-2xl bg-prism-cream px-5 py-4">
                <img src="/prism-logo-full.png" alt="Prism Psychology" className="h-11 w-auto object-contain" />
              </div>
              <p className="mt-6 max-w-sm font-light text-primary-foreground/70">
                Dr. Bairbre Fee · Chartered Senior Clinical Psychologist.
                Compassionate, evidence-based psychology in South Dublin,
                Ireland.
              </p>
            </Reveal>

            <div className="mt-14 grid grid-cols-2 gap-8">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-primary-foreground/50 mb-4">Explore</p>
                <ul className="space-y-3">
                  {NAV.map((n) => (
                    <li key={n.id}>
                      <button
                        data-testid={`footer-nav-${n.id}`}
                        onClick={() => scrollTo(n.id)}
                        className="font-light text-primary-foreground/80 hover:text-primary-foreground transition-colors inline-flex items-center gap-1 group"
                      >
                        {n.label}
                        <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-primary-foreground/50 mb-4">Practice</p>
                <ul className="space-y-3 font-light text-primary-foreground/80">
                  <li>Weekdays 9:00 – 17:00</li>
                  <li>Online &amp; in-person</li>
                  <li>South Dublin, Ireland</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-primary-foreground/15 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-primary-foreground/50">
          <p>© {new Date().getFullYear()} Prism Psychology. All rights reserved.</p>
          <p className="font-light">Seen through a gentle lens.</p>
        </div>
      </div>
    </footer>
  );
};
